import { graphHooks } from '@tunasong/graph-lib/react'
import type { Alert } from '@tunasong/models'
import { logger } from '@tunasong/models'
import { features, useThunkDispatch } from '@tunasong/redux'
import type { Persisted, Rider } from '@tunasong/schemas'
import { useState } from 'react'
import { displayName } from '../readiness.js'
import { buildRemindComment, buildShowLeadComment } from '../remind.js'
import type { Person } from '../remind.js'

/**
 * Remind and Show {lead}: each posts one comment on the rider that mentions the people asked, which notifies them
 * through the existing mention path. Each reports its result, and can be pressed again after a failure.
 */
export const useRiderAsks = () => {
  const { createEntity } = graphHooks.useEntityCreate()
  const dispatch = useThunkDispatch()
  const [posting, setPosting] = useState(false)

  const notify = (alert: Alert) => dispatch(features.notifications.actions.setAlert(alert))

  const post = async (
    rider: Persisted<Rider>,
    entity: ReturnType<typeof buildRemindComment>,
    ok: string,
    failed: string
  ) => {
    setPosting(true)
    try {
      await createEntity({ entity, parent: rider })
      notify({ severity: 'success', message: ok })
    } catch (error) {
      logger.warn('Posting a rider comment failed', { riderId: rider.id, error })
      notify({ severity: 'error', message: failed })
    } finally {
      setPosting(false)
    }
  }

  const remind = (rider: Persisted<Rider>, authorId: string, missing: Person[]) =>
    post(
      rider,
      buildRemindComment({ riderId: rider.id, authorId, missing }),
      `Reminder posted on ${rider.name ?? 'the rider'}.`,
      "Couldn't post the reminder. Check your connection and try again."
    )

  const showLead = (rider: Persisted<Rider>, authorId: string, lead: Person, updatedAt: string) =>
    post(
      rider,
      buildShowLeadComment({ riderId: rider.id, authorId, lead, riderName: rider.name ?? 'The rider', updatedAt }),
      `Posted on ${rider.name ?? 'the rider'}. ${displayName(lead)} will get a notification.`,
      "Couldn't post your update. Check your connection and try again."
    )

  return { posting, remind, showLead }
}
